import React, { useEffect, useState } from "react";
import "./driver.scss";
import { useDispatch } from "react-redux";
import { fetchDriver, removeDriver } from "../../store/slices/Driver/thunk";
import {
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Tooltip,
  Avatar,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { Delete, Search, Add } from "@mui/icons-material";
import NoImage from "../../assets/No-Image-Found-400x264.png";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "../../components/deleteModal";
import Loader from "../../components/loader";
import { toast, ToastContainer } from "react-toastify";
import { FaRegCopy } from "react-icons/fa";
import { useClientFilter } from "../../Hooks/useClientFilter";
import FilterPopover from "../../components/FilterPopover/FilterPopover";

const DriverList = () => {
  const [search, setSearch] = useState("");
  const dispatch: any = useDispatch();
  const [getAllDriver, setAllDriver] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const DEBOUNCE_DELAY = 500;
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [getCurrentUser, setCurrentUser] = useState<any>(null);

  const { filteredData, filters, setFilter, clearFilter } = useClientFilter(getAllDriver);
  const navigate = useNavigate();
  const ITEMS_PER_PAGE = 10;
  const pageCount = Math.ceil(totalCount / ITEMS_PER_PAGE);
  const showActions = getCurrentUser && getCurrentUser.role === "admin" && getCurrentUser.role !== "superAdmin";
  const columnCount = showActions ? 8 : 7;

  const normalizeDriverStatus = (status: any) => {
    if (!status) return "--";
    const lower = String(status).toLowerCase();
    if (lower === "active" || lower.includes("duty")) return "On Duty";
    if (lower === "inactive" || lower.includes("break")) return "On Break";
    return String(status);
  };

  const formatLicense = (license: any) => {
    if (!license) return "--";
    const value = String(license).trim();
    return value.toUpperCase().startsWith("LIC-") ? value : `LIC-${value}`;
  };

  const handleCopy = async (driver: any) => {
    if (!driver?.whatsappLink) return;

    try {
      const url = new URL(driver.whatsappLink);
      const text = url.searchParams.get("text");

      if (text) {
        await navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard!");
      } else {
        toast.success("No text found in link!");
      }
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  useEffect(() => {
    const handler = setTimeout(async () => {
      setIsLoading(true);
      try {
        const response = await dispatch(
          fetchDriver(page, ITEMS_PER_PAGE, search)
        );
        if (
          response &&
          response?.data?.data?.length > 0 &&
          response?.isSuccess
        ) {
          setAllDriver(response?.data?.data);
          setTotalCount(response?.data?.totalCount);
        } else {
          setAllDriver([]);
          setTotalCount(0);
        }
      } catch (error) {
        console.log(getAllDriver);
        setAllDriver([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    }, DEBOUNCE_DELAY);
    return () => {
      clearTimeout(handler);
    };
  }, [search, page, dispatch]);
useEffect(() => {
    const fetchUser = async () => {
        const userData = localStorage.getItem("user");
      if (userData) {
        setCurrentUser(JSON.parse(userData));
      }
    };
    fetchUser();
  }, []);
  const gotoEdit = (id: string) => {
    navigate(`/edit-driver/${id}`);
  };
  const deleteDriver = async (id: string) => {
    const response = await dispatch(removeDriver(id));
    if (response && response.isSuccess) {
      const newDriver = getAllDriver.filter((item: any) => item._id !== id);
      setAllDriver(newDriver);
    }
  };
  const handleDeleteClick = (id: string) => {
    setDeleteId(id);
    setOpenDialog(true);
  };

  const confirmDelete = async () => {
    if (deleteId) {
      await deleteDriver(deleteId);
    }
    setOpenDialog(false);
    setDeleteId(null);
  };
  const navigateTADddriver = () => {
    navigate("/add-driver");
  };

  return (
    <>
      <ToastContainer />
      <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 113px)", overflow: "hidden" }}>
      <Box className="driver-page" sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
        <Box className="driver-header" sx={{ flexShrink: 0 }}>
          <Box className="driver-header-left">
            <h2 style={{ color: "#0f172a", fontWeight: "700", fontSize: "24px", margin: "0", fontFamily: "sans-serif" }}>
              Drivers Management
            </h2>
            <p style={{ color: "#64748b", fontSize: "14px", margin: "4px 0 0 0", fontWeight: "400" }}>
              Manage and list all drivers in your fleet.
            </p>
          </Box>
          <Box className="driver-header-right">
            <Chip
              label={`${totalCount} Total`}
              className="total-count-chip"
            />
            {getCurrentUser && getCurrentUser.role === "admin" && getCurrentUser.role !== "superAdmin" && (
              <Button
                variant="contained"
                startIcon={<Add />}
                className="add-driver-btn"
                onClick={navigateTADddriver}
              >
                Add New Driver
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={{ mb: 2, flexShrink: 0 }}>
          <TextField
            variant="outlined"
            placeholder="Search driver by name or mobile number..."
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              minWidth: 380,
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "#fff",
                height: "40px",
                "& fieldset": { borderColor: "#d0d5dd" },
                "&:hover fieldset": { borderColor: "#7c6dff" },
                "&.Mui-focused fieldset": { borderColor: "#7c6dff" },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: "#98a2b3", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Paper className="driver-table-paper" elevation={0} sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}>
          <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>

            <Box className="table-responsive-wrapper" sx={{ flex: 1, overflow: "hidden" }}>
              <TableContainer sx={{ flex: 1, overflowY: "auto" }}>
                <Table className="driver-table">
                  <TableHead>
                    <TableRow>
                      <TableCell className="th-cell">S.No</TableCell>
                      <TableCell className="th-cell">
                        Driver Name
                        <FilterPopover column="driverName" filterType="text" currentValue={filters.driverName || ""} onFilterChange={setFilter} onClear={clearFilter} />
                      </TableCell>
                      <TableCell className="th-cell">
                        Phone
                        <FilterPopover column="phoneNumber" filterType="text" currentValue={filters.phoneNumber || ""} onFilterChange={setFilter} onClear={clearFilter} />
                      </TableCell>
                      <TableCell className="th-cell">
                        Licence No.
                        <FilterPopover column="licenseNo" filterType="text" currentValue={filters.licenseNo || ""} onFilterChange={setFilter} onClear={clearFilter} />
                      </TableCell>
                      <TableCell className="th-cell">
                        Status
                        <FilterPopover column="driverStatus" filterType="text" currentValue={filters.driverStatus || ""} onFilterChange={setFilter} onClear={clearFilter} />
                      </TableCell>
                      {/* <TableCell className="th-cell">
                        Trips
                        <FilterPopover column="tripCount" filterType="text" currentValue={filters.tripCount || ""} onFilterChange={setFilter} onClear={clearFilter} />
                      </TableCell> */}
                       <TableCell className="th-cell">Driver Details</TableCell>
                      {showActions && (
                        <TableCell className="th-cell">Actions</TableCell>
                      )}
                     
                    </TableRow>
                  </TableHead>
                  {isLoading ? (
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={columnCount} align="center">
                          <Loader />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  ) : (
                    <TableBody>
                      {filteredData.map((item: any, index) => {
                        const driverStatus = normalizeDriverStatus(item.driverStatus);
                        const tripCount =
                          item.tripCount ?? item.totalTrips ?? item.trips?.length;
                        return (
                          <TableRow key={item._id} className="driver-row">
                            <TableCell className="td-cell">
                              {(page - 1) * ITEMS_PER_PAGE + index + 1}
                            </TableCell>
                            <TableCell className="td-cell">
                              <Box className="driver-info-cell">
                                <Avatar
                                  src={item.driverImage || NoImage}
                                  alt={item.driverName}
                                  className="driver-avatar"
                                />
                                <Typography className="driver-name">
                                  {item.driverName || "--"}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell className="td-cell">
                              {item.phoneNumber || "--"}
                            </TableCell>
                            <TableCell className="td-cell">
                              <Typography className="license-number">
                                {formatLicense(item.licenseNo)}
                              </Typography>
                            </TableCell>
                            <TableCell className="td-cell">
                              <Chip
                                label={driverStatus}
                                className={`status-pill ${
                                  driverStatus === "On Duty"
                                    ? "on-duty"
                                    : driverStatus === "On Break"
                                    ? "on-break"
                                    : ""
                                }`}
                              />
                            </TableCell>
                            <TableCell>
                        <Button
                          variant="contained"
                          endIcon={<FaRegCopy style={{ fontSize: "15px" }} />}
                          style={{
                            width: "120px",
                            height: "auto",
                            background: "#f3f4f6",
                            color: "#374151",
                            fontWeight: "500",
                            fontSize: "13px",
                            borderRadius: "12px",
                            textTransform: "none",
                          }}
                          component="a"
                          onClick={() => handleCopy(item)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Copy
                        </Button>
                      </TableCell>
                            {/* <TableCell className="td-cell">
                              <Chip
                                label={
                                  tripCount !== undefined && tripCount !== null
                                    ? `Trip ${tripCount}`
                                    : "--"
                                }
                                className="trip-pill"
                              />
                            </TableCell> */}
                            {showActions && (
                              <TableCell className="td-cell">
                                <Box className="action-icons">
                                  <Tooltip title="Edit">
                                    <IconButton
                                      onClick={() => gotoEdit(item._id)}
                                      className="icon-btn edit-icon-btn"
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton
                                      onClick={() => handleDeleteClick(item._id)}
                                      className="icon-btn delete-icon-btn"
                                    >
                                      <Delete />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            )}
                            
                          </TableRow>
                        );
                      })}

                      {filteredData.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={columnCount} align="center">
                            <Box className="no-data">
                              <Typography variant="h6">
                                No Driver Found
                              </Typography>
                              <Typography variant="body2">
                                Try adjusting your search or add a new driver
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Box>
          </Box>

          <nav style={{ flexShrink: 0 }}>
            <ul className="pagination justify-content-center mt-3">
              <li className={`page-item ${page === 1 && "disabled"}`}>
                <button className="page-link" onClick={() => setPage((p) => Math.max(p - 1, 1))}>Previous</button>
              </li>
              {Array.from({ length: Math.min(5, pageCount) }, (_, i) => (
                <li key={i + 1} className={`page-item ${page === i + 1 && "active"}`}>
                  <button className="page-link" onClick={() => setPage(i + 1)}>{i + 1}</button>
                </li>
              ))}
              {pageCount > 5 && (
                <>
                  <li className="page-item disabled">
                    <span className="page-link">...</span>
                  </li>
                  <li className="page-item">
                    <button className="page-link" onClick={() => setPage(pageCount)}>{pageCount}</button>
                  </li>
                </>
              )}
              <li className={`page-item ${page === pageCount && "disabled"}`}>
                <button className="page-link" onClick={() => setPage((p) => Math.min(p + 1, pageCount))}>Next</button>
              </li>
            </ul>
          </nav>
        </Paper>
      </Box>
      </div>

      <ConfirmDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onConfirm={confirmDelete}
      />
    </>
  );
};

export default DriverList;

